import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { startGoogleLogin } from "@/lib/googleAuth";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Welcome back to Glexis");
      navigate("/dashboard");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Unable to sign in.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#050914]">
      <div className="relative hidden lg:flex flex-col justify-between p-12 gx-grid-bg border-r border-slate-800 overflow-hidden">
        <div className="absolute inset-0 gx-radial" />
        <div className="relative z-10">
          <Logo size="lg" />
        </div>
        <div className="relative z-10 max-w-md">
          <h2 className="font-display text-4xl font-bold tracking-tight text-white leading-tight">
            Clarity, confidence, <span className="text-cyan-400 gx-text-glow">control</span>.
          </h2>
          <p className="mt-4 text-slate-400">
            Communication intelligence that helps you understand every conversation and make
            clearer decisions.
          </p>
        </div>
        <div className="relative z-10 flex items-center gap-2 text-sm text-slate-500">
          <ShieldCheck className="h-4 w-4 text-cyan-400" /> Secure encrypted connections
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8">
            <Logo size="lg" />
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-white">Sign in</h1>
          <p className="mt-2 text-slate-400">Access your communication reports.</p>

          <Button
            type="button"
            onClick={startGoogleLogin}
            data-testid="google-login-btn"
            variant="outline"
            className="mt-8 w-full h-11 border-slate-700 bg-slate-900/40 text-white hover:bg-slate-800 hover:text-white"
          >
            <GoogleIcon /> Continue with Google
          </Button>

          <div className="my-6 flex items-center gap-4 text-xs text-slate-600">
            <span className="h-px flex-1 bg-slate-800" /> OR <span className="h-px flex-1 bg-slate-800" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-11 bg-slate-900/50 border-slate-700 text-white focus-visible:ring-cyan-400"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <Input
                id="password"
                type="password"
                required
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-11 bg-slate-900/50 border-slate-700 text-white focus-visible:ring-cyan-400"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <p className="text-sm text-red-400" data-testid="login-error">{error}</p>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full h-11 bg-cyan-400 text-black font-semibold hover:bg-cyan-300 gx-glow"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-400">
            New to Glexis?{" "}
            <Link to="/signup" data-testid="go-signup" className="text-cyan-400 hover:underline">
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
    </svg>
  );
}
